import { z } from 'zod';
import { DELIVERY_RECORD_ORIGINS } from '../enums.js';
import {
  countryCodeSchema,
  dateRangeFields,
  isoDateSchema,
  isOrderedRange,
  longTextSchema,
  moneySchema,
  ORDERED_RANGE_ISSUE,
  uuidSchema,
} from '../primitives.js';

/**
 * Deliveries.
 *
 * A delivery is an operational job that produces ordinary money: revenue is a
 * sale, the rider's cut is an expense. Nothing here describes a second ledger —
 * it describes the job, and points at the records it created.
 *
 * CenoX does not run the job. Another system does, and sends what happened as
 * a sequence of events. So the contract below is an INTAKE contract: it is
 * shaped by what the operational system knows, not by what a form would ask.
 */

/**
 * Which channel created the job.
 *
 * One of two axes, and the distinction is the whole point. This one answers
 * *where the business transaction came from* — a customer messaging on
 * WhatsApp, tapping in the app, walking up to a counter. The other axis,
 * `record_origin`, answers *how the record reached CenoX*: watched live, or
 * read from a spreadsheet afterwards.
 *
 * Collapsing them loses real information. Ceno Hub's two WhatsApp deliveries
 * arrived through a spreadsheet months later: channel `whatsapp`, record
 * `import`. A single "source" field would have to pick one and throw the other
 * away, and whichever it picked would be a lie about the row.
 *
 * Each value is also an idempotency namespace. The uniqueness key is
 * (business, origin, reference), so two systems that each invent their own
 * references cannot collide by accident.
 */
export const DELIVERY_ORIGINS = [
  /** A customer ordering over WhatsApp. */
  'whatsapp',
  /** A customer or rider ordering in the app. */
  'customer_app',
  /** Entered by a person on a CenoX screen, with the job in front of them. */
  'manual',
  /** Typed into CenoHub's dispatch Shop Entry. */
  'shop',
  /**
   * The channel was never recorded.
   *
   * Not a channel — the absence of one. 569 imported deliveries came from a
   * sheet whose Source column said nothing the importer recognised, and they
   * are stored as `import`, which is a transport rather than an origin. Giving
   * that honest meaning a name is what lets the screen say "not recorded"
   * instead of guessing Manual and being wrong 569 times.
   */
  'unknown',
] as const;
export type DeliveryOrigin = (typeof DELIVERY_ORIGINS)[number];

/**
 * What earlier spellings mean now.
 *
 * `app` and `cenox` are written on real production rows and on nothing else;
 * renaming them in place would rewrite history to tidy a display label, which
 * is a bad trade on a financial record. So they keep their stored value and are
 * read as the canonical name instead — the row is untouched, the screen is
 * consistent, and a filter for Manual finds deliveries entered before the word
 * existed.
 *
 * Write paths canonicalise; read paths accept both. Nothing ever writes an
 * alias again.
 */
export const DELIVERY_ORIGIN_ALIASES: Readonly<Record<string, DeliveryOrigin>> = {
  app: 'customer_app',
  cenox: 'manual',
  /* A transport written into the channel column before the two were separated. */
  import: 'unknown',
};

export const DELIVERY_ORIGIN_LABELS: Record<DeliveryOrigin, string> = {
  whatsapp: 'WhatsApp',
  customer_app: 'App',
  manual: 'Manual',
  shop: 'Shop',
  unknown: 'Not recorded',
};

const isCanonicalOrigin = (v: string): v is DeliveryOrigin =>
  (DELIVERY_ORIGINS as readonly string[]).includes(v);

/**
 * The canonical name for a stored or submitted origin.
 *
 * Returns null for anything unrecognised rather than guessing. A value nobody
 * planned for is a configuration fault worth seeing, not a row to quietly file
 * under the nearest match.
 */
export function canonicalDeliveryOrigin(raw: string | null | undefined): DeliveryOrigin | null {
  const key = String(raw ?? '').trim().toLowerCase();
  if (isCanonicalOrigin(key)) return key;
  return DELIVERY_ORIGIN_ALIASES[key] ?? null;
}

/**
 * Every stored spelling that reads as one canonical origin.
 *
 * What a filter has to match on, because the column still holds the old values:
 * asking for `manual` must find the rows that say `cenox`.
 */
export function storedDeliveryOrigins(origin: DeliveryOrigin): readonly string[] {
  const aliases = Object.entries(DELIVERY_ORIGIN_ALIASES)
    .filter(([, canonical]) => canonical === origin)
    .map(([stored]) => stored);
  return [origin, ...aliases];
}

/** Accepts a canonical name or a legacy alias, and yields the canonical one. */
export const deliveryOriginSchema = z
  .string()
  .trim()
  .transform((v, ctx) => {
    const canonical = canonicalDeliveryOrigin(v);
    if (!canonical) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Unknown delivery source "${v}"`,
      });
      return z.NEVER;
    }
    return canonical;
  });

export const DELIVERY_EVENT_TYPES = [
  /** First application: the job happened and the money is real. */
  'POSTED',
  /** A correction to a delivery already applied. */
  'UPDATED',
  /** The job was cancelled after it had been posted. */
  'CANCELLED',
  /** The financial records are being reversed, the delivery record kept. */
  'FINANCIAL_REVERSED',
] as const;
export type DeliveryEventType = (typeof DELIVERY_EVENT_TYPES)[number];

/**
 * The names a sending system uses, and what each one does here.
 *
 * Ceno Hub's workflow has eight moments worth telling CenoX about; this ledger
 * has four things it can do to a record. The mapping is deliberately many-to-few
 * rather than one-to-one, because `rider_assigned`, `picked_up`, `delivered` and
 * `paid` are the *same operation* on the ledger — carry the sender's new state
 * onto the existing row — and differ only in which fields moved.
 *
 * Giving each its own ledger operation would mean four code paths that must
 * stay in step about money, which is how two of them eventually stop being.
 * What they genuinely change is the delivery's status, and whether a status is
 * worth money is decided in exactly one place: [[DELIVERY_COMPLETE_STATUSES]]
 * and [[DELIVERY_PAID_STATUSES]]. So `delivery.delivered` recognises nothing by
 * being named that — the row it leaves behind does.
 *
 * `delivery.returned` maps to the cancelling operation rather than to an update
 * because a returned parcel ends the job: the revenue is reversed, and what the
 * rider is owed for the distance already ridden is the sender's to state.
 */
export const DELIVERY_EVENT_NAMES = {
  'delivery.created': 'POSTED',
  'delivery.updated': 'UPDATED',
  'delivery.rider_assigned': 'UPDATED',
  'delivery.picked_up': 'UPDATED',
  'delivery.delivered': 'UPDATED',
  'delivery.paid': 'UPDATED',
  'delivery.cancelled': 'CANCELLED',
  'delivery.returned': 'CANCELLED',
} as const satisfies Record<string, DeliveryEventType>;

export type DeliveryEventName = keyof typeof DELIVERY_EVENT_NAMES;

export const DELIVERY_EVENT_NAME_VALUES = Object.keys(
  DELIVERY_EVENT_NAMES,
) as readonly DeliveryEventName[];

const isEventName = (v: string): v is DeliveryEventName =>
  Object.prototype.hasOwnProperty.call(DELIVERY_EVENT_NAMES, v);

const isLedgerEventType = (v: string): v is DeliveryEventType =>
  (DELIVERY_EVENT_TYPES as readonly string[]).includes(v);

/**
 * The ledger operation a sender's event name asks for.
 *
 * The four uppercase names are still accepted, because they are what the
 * contract shipped with and a sender already using them is not wrong.
 */
export function ledgerEventType(name: string): DeliveryEventType | null {
  const key = String(name ?? '').trim();
  if (isEventName(key)) return DELIVERY_EVENT_NAMES[key];
  if (isLedgerEventType(key)) return key;
  return null;
}

/** Accepts either vocabulary and yields the ledger operation. */
export const deliveryEventTypeSchema = z
  .string()
  .trim()
  .transform((v, ctx) => {
    const type = ledgerEventType(v);
    if (!type) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Unknown delivery event type "${v}"`,
      });
      return z.NEVER;
    }
    return type;
  });

/**
 * The state of a delivery as the sending system knows it.
 *
 * `fee` and `riderPayout` are the two stored numbers. The company's share is
 * neither sent nor stored: it is `fee - riderPayout`, and a third number would
 * be a third thing that can disagree with the other two.
 *
 * `riderPayout` is AUTHORITATIVE WHERE IT IS GIVEN. CenoX does not overrule a
 * sender about what a rider earned — on a cancellation the rider may be owed
 * everything, something, or nothing depending on how far they got, and that
 * policy belongs to the dispatch system that knows. Where it is omitted, and
 * only then, CenoX derives one. See deliveries/payout.ts for the single rule
 * both the ingestion and counter paths now run.
 */
export const deliverySnapshotSchema = z.object({
  /** The location this job belongs to, by immutable code — never a name. */
  branchCode: z.string().trim().min(1).max(32),
  serviceType: z.string().trim().max(80).nullable().optional(),
  pickup: longTextSchema.nullable().optional(),
  destination: longTextSchema.nullable().optional(),

  customerName: z.string().trim().max(200).nullable().optional(),
  customerPhone: z.string().trim().max(32).nullable().optional(),
  /**
   * Who the parcel is for, and who owes the fee — the two things a counter
   * entry already records (migration 0111). `customerName` stays the sender.
   * Optional: a food order has no separate receiver to give, and omitting
   * them on a later event leaves what an earlier one said alone.
   */
  receiverName: z.string().trim().max(200).nullable().optional(),
  receiverPhone: z.string().trim().max(32).nullable().optional(),
  paidBy: z.enum(['sender', 'receiver']).nullable().optional(),

  riderExternalId: z.string().trim().max(64).nullable().optional(),
  riderName: z.string().trim().max(200).nullable().optional(),
  riderPhone: z.string().trim().max(32).nullable().optional(),

  fee: moneySchema,
  /**
   * What the rider earned, where the sender knows.
   *
   * Optional, and the distinction is load-bearing. A sender that states a
   * payout is authoritative — only it knows what a job cancelled halfway owes
   * somebody. A sender that omits it is saying it has no opinion, and CenoX
   * derives one from the flat company cut, exactly as the counter form does.
   *
   * An explicit `"0.00"` is a statement, not an omission: a rider owed nothing
   * for a job they never started. See deliveries/payout.ts.
   */
  riderPayout: moneySchema.nullable().optional(),

  paymentMethod: z.string().trim().max(40).nullable().optional(),
  paymentStatus: z.string().trim().max(40).nullable().optional(),
  deliveryStatus: z.string().trim().max(40).nullable().optional(),
  paymentProofRef: z.string().trim().max(400).nullable().optional(),
  cancelReason: z.string().trim().max(400).nullable().optional(),
  occurredAt: z.string().datetime().optional(),
  /**
   * The job's own order number in the system the customer used — a WhatsApp
   * order's CENO-… code — when it differs from `externalRef` (which stays the
   * sender's stable id and is what CenoX keys the delivery on). Stored as the
   * delivery's dispatch reference: searchable, and the number a counter entry
   * of the same job is matched on, so one job is counted once.
   */
  dispatchRef: z.string().trim().min(1).max(120).nullable().optional(),
  /** Base or Fast, as the customer chose. Omitted leaves what is on file. */
  deliveryType: z.enum(['base', 'fast']).nullable().optional(),
});
export type DeliverySnapshot = z.infer<typeof deliverySnapshotSchema>;

/** One lifecycle event about one delivery. */
export const deliveryEventSchema = z.object({
  /** The sender's idempotency key. Replaying it is a no-op, never an error. */
  eventId: uuidSchema,
  externalRef: z.string().trim().min(1).max(128),
  originSource: deliveryOriginSchema,
  /** 1, 2, 3 … per delivery. Gaps are held, never skipped. */
  version: z.number().int().positive(),
  eventType: deliveryEventTypeSchema,
  snapshot: deliverySnapshotSchema,
  reason: z.string().trim().max(400).nullable().optional(),
});
export type DeliveryEventInput = z.infer<typeof deliveryEventSchema>;

/**
 * The vocabularies a delivery is entered with.
 *
 * Taken from the dispatch Shop Entry unchanged, down to the wording. Staff have
 * been choosing from these lists for months; renaming "Errand/Personal Shopper"
 * to something tidier would make every historical record read differently from
 * the ones beside it, for no gain to anybody doing the job.
 */
export const DELIVERY_SERVICE_TYPES = [
  'Food Delivery',
  'Parcel Delivery',
  'Grocery Run',
  'Errand/Personal Shopper',
  'Document Delivery',
  'Bulk/Corporate',
] as const;

/**
 * "Cash to Rider" means the customer handed the whole fee to the rider, so the
 * rider is holding the company's share of it. It changes no figure on the
 * delivery — the settlement offsets what the rider holds instead.
 */
export const DELIVERY_PAYMENT_METHODS = [
  'Cash',
  'Cash to Rider',
  'Bank Transfer',
  'POS/Card',
  'Mobile Wallet',
  'Wallet Credit',
] as const;

export const DELIVERY_PAYMENT_STATUSES = ['Unpaid', 'Paid', 'Part Payment', 'Refunded'] as const;

export const DELIVERY_STATUSES = [
  /*
    A job on the books with nobody carrying it yet.

    First in the list because it is where a counter-recorded delivery starts:
    the parcel and the money are known before the rider is. It is deliberately
    NOT in DELIVERY_COMPLETE_STATUSES or RIDER_REQUIRED_STATUSES — nothing is
    finished, nothing is recognised, and no rider is demanded until one is
    actually being assigned.
  */
  'Awaiting Rider',
  'Pending',
  'Assigned',
  'In Transit',
  'Delivered',
  'Cancelled',
  'Failed/Returned',
] as const;

/**
 * Statuses that mean the work is finished and the money can be recognised.
 *
 * `Delivered` is CenoX's own canonical value, in DELIVERY_STATUSES above.
 * `Completed` is not: it is what the CenoHub staging workbook and the App that
 * feeds it write, on all 1,045 of its rows. Treating the two as one thing here —
 * rather than adding `Completed` to the canonical list — keeps the vocabulary a
 * CenoX user picks from unchanged while letting an imported record be recognised
 * for what it plainly is.
 *
 * Deliberately shared between the posting path (`shouldPost` in
 * deliveries/entry.ts) and the Logistics report. Two definitions of "finished"
 * would drift, and the drift would show up as a report that recognises revenue
 * the ledger never posted.
 *
 * Comparison is case-insensitive and trimmed at the point of use; these are the
 * spellings, not a parser.
 */
export const DELIVERY_COMPLETE_STATUSES: readonly string[] = ['Delivered', 'Completed'];

/** SQL-quoted list, for predicates that run in the database. */
export const DELIVERY_COMPLETE_STATUSES_SQL: readonly string[] = DELIVERY_COMPLETE_STATUSES;

/**
 * Statuses that mean the job is still moving: booked, waiting for a rider,
 * with one, or on the road.
 *
 * The third bucket beside complete and stopped (Cancelled, Failed/Returned).
 * Every status in DELIVERY_STATUSES lands in exactly one of the three, which
 * is what lets the Deliveries screen show Active and Completed beside a
 * History total without counting a job twice. A status nobody planned for
 * (an import's own spelling) is in none of them: it still counts in History,
 * and is not claimed as moving or finished.
 */
export const DELIVERY_ACTIVE_STATUSES: readonly string[] = [
  'Awaiting Rider',
  'Pending',
  'Assigned',
  'In Transit',
];

/**
 * Statuses that mean the job has stopped without finishing.
 *
 * The third bucket. Neither moving nor delivered, so neither Active nor
 * Completed on the board — a cancelled job is not work in progress and a
 * returned one is not revenue. Stopping a job reverses its revenue, so it
 * leaves no collectible balance either, whatever its payment status says —
 * the Logistics receivable (`earned_unpaid` in deliveries/finance.ts) agrees.
 */
export const DELIVERY_STOPPED_STATUSES: readonly string[] = ['Cancelled', 'Failed/Returned'];

/**
 * Payment statuses that mean somebody still owes for this job.
 *
 * `Part Payment` sits here with `Unpaid` because a balance is a balance: the
 * job stays on the board until it reaches zero. `Refunded` is NOT here — the
 * money went back, so nothing is outstanding and nothing is collectable. A
 * refunded delivery is finished business in both directions, which is why it
 * appears in History and on no operational card.
 */
export const DELIVERY_OUTSTANDING_PAYMENT_STATUSES: readonly string[] = ['Unpaid', 'Part Payment'];

/** Does somebody still owe for this job? Compared the forgiving way, like the rest. */
export function hasOutstandingPayment(status: string | null | undefined): boolean {
  const s = (status ?? '').trim().toLowerCase();
  return DELIVERY_OUTSTANDING_PAYMENT_STATUSES.some((p) => p.toLowerCase() === s);
}

/**
 * Is there a customer balance worth chasing on this job?
 *
 * The browser's copy of the API's rule (deliveries/read.ts, `OUTSTANDING`):
 * not paid, not stopped, not a duplicate, and a fee above zero at an
 * authorised price. Decides only whether a row shows what is owed; the cards
 * and the list are counted by the API alone.
 */
export function hasCollectibleBalance(d: {
  deliveryStatus: string | null;
  paymentStatus: string | null;
  fee: string;
  feeStatus: string | null;
  supersededByDeliveryId: string | null;
}): boolean {
  return (
    hasOutstandingPayment(d.paymentStatus) &&
    !isStoppedDelivery(d.deliveryStatus) &&
    !d.supersededByDeliveryId &&
    Number(d.fee) > 0 &&
    ['standard', 'exception_approved'].includes(d.feeStatus ?? 'standard')
  );
}

/** Has the job stopped without finishing? */
export function isStoppedDelivery(status: string | null | undefined): boolean {
  const s = (status ?? '').trim().toLowerCase();
  return DELIVERY_STOPPED_STATUSES.some((p) => p.toLowerCase() === s);
}

/** Is the job finished, by either spelling the two systems use? */
export function isDeliveredStatus(status: string | null | undefined): boolean {
  const s = (status ?? '').trim().toLowerCase();
  return DELIVERY_COMPLETE_STATUSES.some((p) => p.toLowerCase() === s);
}

// ── Deadlines ───────────────────────────────────────────────────────────────

/**
 * How long a delivery may stay open before it is overdue.
 *
 * Resolved most-specific-first: service type and state, then service type and
 * country, then service type, then the business-wide fallback (no service
 * type). A business that sets nothing has no deadline, and a job's age is then
 * reported without calling it late — "old" and "overdue" are different claims
 * and only one of them needs a rule.
 *
 * Deliberately not effective-dated: a deadline decides whether a job is late
 * right now, so editing it is meant to change what the board says today.
 */
export const slaRuleInputSchema = z.object({
  /** Null, or absent, sets the business-wide fallback. */
  serviceType: z.enum(DELIVERY_SERVICE_TYPES).nullish(),
  countryCode: z.string().trim().length(2).toUpperCase().nullish(),
  /** Needs a country: the same state code is used by more than one country. */
  stateCode: z.string().trim().min(2).max(8).toUpperCase().nullish(),
  /** Whole hours, up to a year. */
  hours: z.number().int().min(1).max(8760),
  note: longTextSchema.optional(),
});
export type SlaRuleInput = z.infer<typeof slaRuleInputSchema>;

export const slaRuleViewSchema = z.object({
  id: uuidSchema,
  serviceType: z.string().nullable(),
  countryCode: z.string().nullable(),
  stateCode: z.string().nullable(),
  hours: z.number().int(),
  note: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type SlaRuleView = z.infer<typeof slaRuleViewSchema>;

/**
 * The scope a deadline is written for. Null in any field means "any".
 *
 * Structural rather than the stored row, so the resolver below takes both the
 * API's loaded rules and the list the browser fetched.
 */
export interface SlaRuleScope {
  serviceType: string | null;
  countryCode: string | null;
  stateCode: string | null;
  hours: number;
}

/**
 * How specific one rule is. Higher wins.
 *
 * Scored rather than assumed from the order the rules arrived in: the server
 * does order them most-specific-first, but the browser gets them over JSON and
 * a resolver that silently depends on array order is one refactor away from
 * picking the business-wide fallback over a state rule. The weights reproduce
 * that same ordering — service type outranks country, which outranks state —
 * so the two halves of the product cannot disagree about which deadline
 * applies.
 */
function specificity(rule: SlaRuleScope): number {
  return (
    (rule.serviceType !== null ? 4 : 0) +
    (rule.countryCode !== null ? 2 : 0) +
    (rule.stateCode !== null ? 1 : 0)
  );
}

/**
 * The deadline that applies to one delivery, or null when none does.
 *
 * Pure, and given the rules rather than fetching them, so a list of three
 * hundred rows resolves three hundred deadlines without another round trip.
 */
export function slaRuleFor<R extends SlaRuleScope>(
  rules: readonly R[],
  delivery: { serviceType: string | null; countryCode: string | null; stateCode: string | null },
): R | null {
  let best: R | null = null;
  for (const rule of rules) {
    if (rule.serviceType !== null && rule.serviceType !== delivery.serviceType) continue;
    if (rule.countryCode !== null && rule.countryCode !== delivery.countryCode) continue;
    if (rule.stateCode !== null && rule.stateCode !== delivery.stateCode) continue;
    if (best === null || specificity(rule) > specificity(best)) best = rule;
  }
  return best;
}

/**
 * Is the job still moving?
 *
 * Deliberately NOT called "active" any more. The Deliveries board counts a
 * delivered-but-unpaid job as Active, because the work of collecting is still
 * outstanding — but such a job is not *open*, and the difference decides two
 * things: whether a deadline can be missed, and whether an age is worth
 * showing. A delivered job is neither late nor ageing; it is delivered and
 * owed for. See `DASHBOARD_ACTIVE` in the API's read.ts for the wider sense.
 */
export function isOperationallyOpen(status: string | null | undefined): boolean {
  const s = (status ?? '').trim().toLowerCase();
  return DELIVERY_ACTIVE_STATUSES.some((a) => a.toLowerCase() === s);
}

/**
 * What the board can say about one open delivery.
 *
 * `openHours` is always answerable — it is elapsed time, arithmetic on two
 * instants. `overdue` is a claim about the business's own rule, so it is false
 * whenever no rule applies: an old job in a business that set no deadline is
 * old, and saying it is late would be inventing the deadline.
 */
export interface DeliveryAge {
  /** Hours the job has been open, floored. Never negative. */
  openHours: number;
  /** The deadline that applies, in hours, or null when the business set none. */
  deadlineHours: number | null;
  /** True only when a deadline applies and the job has outrun it. */
  overdue: boolean;
  /** Hours past the deadline. 0 when not overdue, or when there is no rule. */
  overdueByHours: number;
}

export function deliveryAge<R extends SlaRuleScope>(
  delivery: {
    deliveryStatus: string | null;
    serviceType: string | null;
    countryCode: string | null;
    stateCode: string | null;
    openedAt: string | Date;
  },
  rules: readonly R[],
  now: Date,
): DeliveryAge {
  const opened = delivery.openedAt instanceof Date ? delivery.openedAt : new Date(delivery.openedAt);
  /*
    Clamped at zero. A delivery posted a minute into the future — a clock skew
    between CenoHub and here, which has happened — would otherwise read as
    "open for -1 hours", and a negative age would compare as under every
    deadline anyway. Zero is the honest answer for a job that has just started.
  */
  const elapsedMs = Math.max(0, now.getTime() - opened.getTime());
  const openHours = Math.floor(elapsedMs / 3_600_000);

  const rule = isOperationallyOpen(delivery.deliveryStatus) ? slaRuleFor(rules, delivery) : null;
  const deadlineHours = rule?.hours ?? null;
  /*
    Compared in milliseconds, not in the floored hours: a 1-hour deadline on a
    job open 1h59m is late, and flooring first would have called it exactly on
    time for another hour.
  */
  const overdue = deadlineHours !== null && elapsedMs > deadlineHours * 3_600_000;

  return {
    openHours,
    deadlineHours,
    overdue,
    overdueByHours: overdue ? openHours - deadlineHours! : 0,
  };
}

/**
 * An elapsed stretch as somebody would say it: "4 hours", "3 days", "2 days 5h".
 *
 * Days once it passes one, because "open for 97 hours" is a number nobody
 * converts in their head — and the hours stay on beside the days, since the
 * difference between 2 days and 2 days 20 hours decides whether anybody rings
 * the rider this morning.
 */
export function openForLabel(hours: number): string {
  if (hours < 1) return 'under an hour';
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'}`;
  const days = Math.floor(hours / 24);
  const rest = hours % 24;
  const d = `${days} day${days === 1 ? '' : 's'}`;
  return rest === 0 ? d : `${d} ${rest}h`;
}

/**
 * The four cards on the Deliveries screen.
 *
 * `history` is every delivery in scope, so it is the absence of a stage
 * filter rather than a filter of its own.
 */
export const DELIVERY_STAGES = ['today', 'active', 'completed', 'history'] as const;
export type DeliveryStage = (typeof DELIVERY_STAGES)[number];

/** Columns the delivery list can be ordered by, on the server, across pages. */
export const DELIVERY_SORTS = ['date', 'reference', 'type', 'amount'] as const;
export type DeliverySort = (typeof DELIVERY_SORTS)[number];

/**
 * Spellings that mean the customer has paid.
 *
 * The other half of "finished", and it has to be as forgiving as the half
 * above. `Delivered`/`Completed` were already compared case-insensitively;
 * payment status was compared with `=== 'Paid'`, exactly. That asymmetry was
 * invisible for as long as CenoX wrote both values itself — the live path
 * stores the canonical `Paid`, so it never meets another casing.
 *
 * The importer does meet them. It keeps the sheet's own spelling verbatim, and
 * the CenoHub daily books write **PAID**. All 569 historical rides would have
 * imported, reported ₦1,698,000 of gross against ₦0 of revenue, and reconciled
 * to nothing — not because a rule was wrong but because of two capital letters.
 *
 * Shared by `shouldPost` in deliveries/entry.ts and the Logistics report, for
 * the same reason [[DELIVERY_COMPLETE_STATUSES]] is: a report that recognises
 * revenue the ledger never posted is the drift worth preventing.
 */
export const DELIVERY_PAID_STATUSES: readonly string[] = ['Paid'];

/**
 * Payment methods that need no screenshot.
 *
 * Cash reached a person, not an account: there is no transfer to evidence. Any
 * other method leaves a record somewhere, and a claim that money arrived should
 * be checkable against it.
 */
export const PROOF_EXEMPT_METHODS: readonly string[] = ['Cash', 'Cash to Rider'];

/** Who owes the fee. Says nothing about whether it has been paid. */
export const DELIVERY_PAID_BY = ['sender', 'receiver'] as const;
export type DeliveryPaidBy = (typeof DELIVERY_PAID_BY)[number];

/** A delivery status from which the rider must already be named. */
export const RIDER_REQUIRED_STATUSES: readonly string[] = ['Assigned', 'In Transit', 'Delivered'];

export const deliveryEntrySchema = z.object({
  branchId: uuidSchema,
  serviceType: z.enum(DELIVERY_SERVICE_TYPES),
  /*
    The SENDER. The field is named `customerName` because that is the column it
    has always written to; the form calls it "Sender name", which is what it
    has always meant for a courier. Renaming the wire field would break the
    dispatch integration and every import mapping for a label.
  */
  customerName: z.string().trim().min(1).max(200),
  customerPhone: z.string().trim().min(7).max(32),
  /** Who the parcel is for. Compulsory on this path, absent on imports. */
  receiverName: z.string().trim().min(1).max(200),
  receiverPhone: z.string().trim().min(7).max(32),
  pickup: z.string().trim().min(1).max(400),
  destination: z.string().trim().min(1).max(400),
  /**
   * Who owes the fee.
   *
   * Deliberately NOT a payment: choosing `receiver` says who will be billed,
   * not that anybody has paid. `paymentStatus` is the only field that says
   * that, and nothing here may move it.
   */
  paidBy: z.enum(DELIVERY_PAID_BY),
  /** What the customer is charged. The rider's cut is derived from it. */
  fee: moneySchema,
  /*
    The rider, compulsory on this path.

    A delivery recorded at a counter now names who is carrying it. The three
    boxes are one choice from the registry, not three strings: the Rider ID and
    name are required here, and `riderId` below is what makes it a registered
    rider rather than a typed one.

    `riderPhone` stays optional IN THE SCHEMA on purpose, and is not a
    loosening. A rider the registry knows has no phone yet may be assigned
    without one — the rule added for riders on the books before Rider IDs
    existed. Whether a blank is allowed depends on that rider's own record, so
    only `checkChosenRider` can decide it; a flat `.min(1)` here would refuse
    a rider the business has deliberately said it is still collecting a number
    for.
  */
  riderName: z.string().trim().max(200).nullable().optional(),
  riderPhone: z.string().trim().max(32).nullable().optional(),
  riderExternalId: z.string().trim().max(64).nullable().optional(),
  /**
   * The rider, as the registry knows them — the stable id, not a spelling.
   *
   * Required whenever a rider is named. The name, phone and Rider ID sent with
   * it must be that rider's own; they are what the counter saw when choosing,
   * and a mismatch means the form and the registry disagree about who this is.
   *
   * NOT made unconditionally required when the rider boxes became compulsory
   * on the form. Compatibility mode exists for riders who were on the books
   * before Rider IDs did, and forcing an id here would refuse every one of
   * them until somebody re-registered them. Whether a registry match is
   * required is the business's own setting, and `checkChosenRider` is the only
   * thing that can read it — the form asks for all three boxes, the server
   * decides what counts as a valid answer.
   */
  riderId: uuidSchema.nullable().optional(),
  paymentMethod: z.enum(DELIVERY_PAYMENT_METHODS),
  paymentStatus: z.enum(DELIVERY_PAYMENT_STATUSES),
  deliveryStatus: z.enum(DELIVERY_STATUSES),
  distanceKm: z.coerce.number().nonnegative().nullable().optional(),
  fuelCost: moneySchema.nullable().optional(),
  notes: longTextSchema.nullable().optional(),
  cancelReason: z.string().trim().max(400).nullable().optional(),
  /** An uploaded attachment id. Required to claim Paid on a method that leaves a record. */
  paymentProofAttachmentId: uuidSchema.nullable().optional(),
  /**
   * When the job happened. Today, unless whoever records it may approve
   * exceptions; an earlier day goes to them for approval with `dateReason`.
   */
  occurredAt: z.string().datetime().optional(),
  /** Why the fee is outside the approved prices. Required to ask for an exception. */
  priceReason: z.string().trim().min(3).max(400).optional(),
  /** Why the job is dated before today. Required to ask for a backdate. */
  dateReason: z.string().trim().min(3).max(400).optional(),
  /**
   * The dispatch job number, when the job also came through dispatch.
   *
   * One job, one set of books: a counter entry naming a dispatch job that is
   * already recorded is refused, and a dispatch event for a job a counter has
   * already recorded is kept for audit without posting a second time.
   */
  dispatchRef: z.string().trim().min(1).max(120).nullable().optional(),
  /** Base (standard) or Fast (priority). Optional; null when not stated. */
  deliveryType: z.enum(['base', 'fast']).nullable().optional(),
  /**
   * Generated by the form once per submission. A second submission with the
   * same value — a double tap, a retry on a bad connection — returns the
   * delivery the first one made instead of recording it twice.
   */
  clientRequestId: uuidSchema.optional(),
  /** The owner's zone the job is in, if the business prices by zone. */
  zoneId: uuidSchema.nullable().optional(),
});
export type DeliveryEntryInput = z.infer<typeof deliveryEntrySchema>;

/** Everything on the entry may be corrected while it is still being worked. */
export const deliveryEntryUpdateSchema = deliveryEntrySchema
  .partial()
  .omit({ branchId: true, clientRequestId: true });
export type DeliveryEntryUpdateInput = z.infer<typeof deliveryEntryUpdateSchema>;

/** How an event was received. Every outcome is explicit; none is a silent drop. */
export const DELIVERY_EVENT_OUTCOMES = [
  /** Applied for the first time. */
  'applied',
  /** Seen before. Nothing changed, and that is success. */
  'duplicate',
  /** Older than what is already applied. Ignored on purpose. */
  'stale',
  /** Its predecessor has not arrived. Held, and retryable. */
  'version_gap',
] as const;
export type DeliveryEventOutcome = (typeof DELIVERY_EVENT_OUTCOMES)[number];

export const deliveryEventResultSchema = z.object({
  outcome: z.enum(DELIVERY_EVENT_OUTCOMES),
  deliveryId: uuidSchema.nullable(),
  appliedVersion: z.number().int(),
  /** Set when the outcome is `version_gap`, so the sender can say what is missing. */
  expectedVersion: z.number().int().optional(),
  receivedVersion: z.number().int().optional(),
});
export type DeliveryEventResult = z.infer<typeof deliveryEventResultSchema>;

/** One proof-of-payment file, as Delivery Details shows it. */
export const deliveryProofFileSchema = z.object({
  attachmentId: uuidSchema,
  filename: z.string(),
  mimeType: z.string(),
  byteSize: z.number().int(),
  uploadedAt: z.string(),
  uploadedByName: z.string().nullable(),
});
export type DeliveryProofFile = z.infer<typeof deliveryProofFileSchema>;

/**
 * Whether a delivery's rider payout has been checked against what the rider
 * was actually owed.
 *
 * A review status, and only that: it is separate from the customer's payment
 * (`paymentStatus`, `paymentVerification`) and nothing reads it to change a
 * fee, a payout, a sale or an expense. A correction to the money is a separate,
 * deliberate act.
 */
export const PAYOUT_VERIFICATION_STATUSES = ['verified', 'under_review', 'not_verified'] as const;
export type PayoutVerificationStatus = (typeof PAYOUT_VERIFICATION_STATUSES)[number];

export const PAYOUT_VERIFICATION_LABELS: Record<PayoutVerificationStatus, string> = {
  verified: 'Verified',
  under_review: 'Under Review',
  not_verified: 'Not Verified',
};

/**
 * Setting it. A note is required for anything but Verified: "under review" or
 * "not verified" without a reason is a flag nobody can act on.
 */
export const payoutVerificationInputSchema = z
  .object({
    status: z.enum(PAYOUT_VERIFICATION_STATUSES),
    note: z.string().trim().max(2000).nullable().optional(),
  })
  .superRefine((v, ctx) => {
    if (v.status !== 'verified' && (v.note ?? '').trim().length < 3) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['note'],
        message: 'Say why — what is being checked, or what was not confirmed',
      });
    }
  });
export type PayoutVerificationInput = z.infer<typeof payoutVerificationInputSchema>;

export const payoutVerificationSchema = z.object({
  status: z.enum(PAYOUT_VERIFICATION_STATUSES),
  note: z.string().nullable(),
  /** Who set it, by name — the question an owner asks first. */
  setByName: z.string().nullable(),
  setByUserId: z.string().uuid().nullable(),
  setAt: z.string().nullable(),
});
export type PayoutVerification = z.infer<typeof payoutVerificationSchema>;

export const deliverySchema = z.object({
  id: uuidSchema,
  /** Canonicalised on read: a row storing `cenox` is presented as `manual`. */
  originSource: z.enum(DELIVERY_ORIGINS),
  /** Watched live, or read from a spreadsheet afterwards. */
  recordOrigin: z.enum(DELIVERY_RECORD_ORIGINS),
  externalRef: z.string(),
  branchId: uuidSchema,
  branchName: z.string().nullable(),
  serviceType: z.string().nullable(),
  pickup: z.string().nullable(),
  destination: z.string().nullable(),
  customerId: uuidSchema.nullable(),
  customerName: z.string().nullable(),
  customerPhone: z.string().nullable(),
  /** Null on anything recorded before these existed, and on imports. */
  receiverName: z.string().nullable(),
  receiverPhone: z.string().nullable(),
  paidBy: z.enum(DELIVERY_PAID_BY).nullable(),
  riderExternalId: z.string().nullable(),
  riderName: z.string().nullable(),
  riderPhone: z.string().nullable(),
  /** The registry rider this delivery belongs to. Null where nobody has said yet. */
  riderId: uuidSchema.nullable(),
  fee: z.string(),
  riderPayout: z.string(),
  /** Derived: `fee - riderPayout`. Never stored, so it cannot disagree. */
  companyShare: z.string(),
  paymentMethod: z.string().nullable(),
  paymentStatus: z.string().nullable(),
  deliveryStatus: z.string().nullable(),
  paymentProofRef: z.string().nullable(),
  cancelReason: z.string().nullable(),
  saleId: uuidSchema.nullable(),
  saleReference: z.string().nullable(),
  expenseId: uuidSchema.nullable(),
  expenseReference: z.string().nullable(),
  distanceKm: z.string().nullable(),
  fuelCost: z.string().nullable(),
  notes: z.string().nullable(),
  paymentProofAttachmentId: uuidSchema.nullable(),
  /** The person who entered it. An owner asking "who recorded this" gets a name. */
  recordedByName: z.string().nullable(),
  /** Who recorded it. A counter may only amend its own, so the screen needs
   *  the identity rather than the display name to decide what to offer. */
  recordedByUserId: z.string().uuid().nullable(),
  appliedVersion: z.number().int(),
  postedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),

  /*
    Four separate questions, answered separately. See DELIVERY_PENDING_VERIFICATION.

    Null on records made before these controls existed: they were never asked,
    and nothing here pretends otherwise.
  */
  /** Was the fee within the approved prices, or an exception — and decided how. */
  feeStatus: z.enum(['standard', 'exception_pending', 'exception_approved', 'exception_rejected']).nullable(),
  /** How the claimed payment stands. */
  paymentVerification: z
    .enum(['cash_reported', 'rider_held', 'pending', 'verified', 'rejected'])
    .nullable(),
  /** Whether the work has earned its fee, independent of whether it was paid. */
  revenueState: z.enum(['earned', 'pending_approval', 'not_completed', 'cancelled', 'superseded']),
  /** When the job happened, if that was recorded separately from when it was entered. */
  occurredAt: z.string().nullable(),
  dispatchRef: z.string().nullable(),
  /** Base or Fast, as the customer chose; null when not stated. Optional for older readers. */
  deliveryType: z.enum(['base', 'fast']).nullable().optional(),
  /**
   * Fee Mismatch — Needs Review: this counter record names a CENO order that
   * also arrived from WhatsApp with a different amount (or both are counting).
   * Null when never flagged. Optional for older readers.
   */
  feeReview: z
    .object({
      status: z.enum(['needs_review', 'resolved']),
      kind: z.enum(['fee_mismatch', 'duplicate_reference']),
      otherDeliveryId: uuidSchema.nullable(),
      otherReference: z.string().nullable(),
      otherPaymentStatus: z.string().nullable(),
      recordedAmount: z.string(),
      verifiedAmount: z.string(),
      difference: z.string(),
      flaggedAt: z.string().nullable(),
      resolution: z.enum(['amount_confirmed', 'corrected_separately', 'marked_duplicate']).nullable(),
      note: z.string().nullable(),
      resolvedAt: z.string().nullable(),
      resolvedByName: z.string().nullable(),
    })
    .nullable()
    .optional(),
  /** Whether the rider payout was checked. Null when nobody has looked. Optional for older readers. */
  payoutVerification: payoutVerificationSchema.nullable().optional(),
  zoneId: uuidSchema.nullable(),
  zoneName: z.string().nullable(),
  /**
   * The currency this delivery's fee is in: its location's, falling back to
   * the business's. Optional so a reader built before it existed still parses.
   */
  currencyCode: z.string().optional(),
  /** Approval requests still waiting for a decision. */
  pendingApprovals: z.array(z.enum(['price', 'backdate', 'payment'])),
  supersededByDeliveryId: uuidSchema.nullable(),
  /**
   * What the sales ledger says is still owed on this job, or null.
   *
   * The authoritative balance, read from the sale and its payments — the same
   * expression the Money report's outstanding uses. Null means there is
   * nothing authoritative to read: no sale was posted, or the sale was voided.
   * Null is not zero and it is not the fee; a screen showing a balance must
   * show this or show nothing, never a figure of its own making.
   */
  ledgerOutstanding: moneySchema.nullable(),
  /**
   * The proof of payment on file, described — never the storage key.
   *
   * Only on the single-delivery read: a list does not need it, and describing
   * every row's file would be a join the list never uses. `current` is what
   * `paymentProofAttachmentId` points at; `earlier` are files this delivery
   * claimed before a newer one replaced them, kept because they are evidence.
   */
  paymentProof: z
    .object({
      current: deliveryProofFileSchema.nullable(),
      earlier: z.array(deliveryProofFileSchema),
    })
    .optional(),
});
export type Delivery = z.infer<typeof deliverySchema>;

/**
 * Delivery completion, revenue recognition, payment verification and cash
 * reconciliation are four different facts.
 *
 *   - Completion: the job was done (`deliveryStatus`).
 *   - Earned: done, at a price somebody authorised (`revenueState`). Unpaid
 *     does not mean unearned — a delivered, correctly priced job the customer
 *     still owes for is earned and receivable.
 *   - Payment: claimed by the counter, and for anything electronic verified
 *     by somebody else before it is posted (`paymentVerification`).
 *   - Reconciliation: cash at the counter is proven by the cash count; cash a
 *     rider holds is proven by the rider's settlement.
 *
 * What the ledger posts, and what the reports count, is unchanged by this
 * file: a delivery is posted once it is completed, its fee is authorised and
 * its payment is Paid. Changing reports to count earned-but-unpaid work is a
 * separate decision, because it would change every historical total.
 *
 * `Pending Verification` is the payment status of a payment claimed on a
 * method that leaves a record, waiting for somebody to check it. It is not
 * Paid, so nothing counts it as money until it is.
 */
export const DELIVERY_PENDING_VERIFICATION = 'Pending Verification';

// ── Approved prices and their exceptions ────────────────────────────────────

export const deliveryPriceRuleInputSchema = z
  .object({
    serviceType: z.enum(DELIVERY_SERVICE_TYPES),
    /** Null: every location. */
    branchId: uuidSchema.nullable().optional(),
    /** Null: every zone, and deliveries that name none. */
    zoneId: uuidSchema.nullable().optional(),
    minFee: moneySchema,
    maxFee: moneySchema,
    /**
     * The first day the price applies, in the business's timezone. Today when
     * omitted; a later day schedules it. Never earlier than today — a price
     * cannot be set retroactively, because deliveries already recorded were
     * judged by the price that applied then.
     */
    effectiveFrom: isoDateSchema.optional(),
    note: z.string().trim().max(400).optional(),
  })
  .refine((r) => Number(r.minFee) <= Number(r.maxFee), {
    message: 'The lowest price cannot be above the highest',
    path: ['minFee'],
  });
export type DeliveryPriceRuleInput = z.infer<typeof deliveryPriceRuleInputSchema>;

export const deliveryPriceRuleSchema = z.object({
  id: uuidSchema,
  serviceType: z.string(),
  branchId: uuidSchema.nullable(),
  branchName: z.string().nullable(),
  zoneId: uuidSchema.nullable(),
  zoneName: z.string().nullable(),
  minFee: z.string(),
  maxFee: z.string(),
  effectiveFrom: z.string(),
  effectiveTo: z.string().nullable(),
  /** Where the price stands today. `withdrawn`: ended before it ever applied. */
  status: z.enum(['scheduled', 'current', 'ended', 'withdrawn']),
  note: z.string().nullable(),
  createdByName: z.string().nullable(),
  createdAt: z.string(),
});
export type DeliveryPriceRule = z.infer<typeof deliveryPriceRuleSchema>;

export const deliveryZoneInputSchema = z.object({
  name: z.string().trim().min(1).max(80),
  /** Null: offered from every location. */
  branchId: uuidSchema.nullable().optional(),
  description: z.string().trim().max(400).optional(),
});
export type DeliveryZoneInput = z.infer<typeof deliveryZoneInputSchema>;

export const deliveryZoneSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  branchId: uuidSchema.nullable(),
  branchName: z.string().nullable(),
  description: z.string().nullable(),
  retiredAt: z.string().nullable(),
});
export type DeliveryZone = z.infer<typeof deliveryZoneSchema>;

export const DELIVERY_APPROVAL_KINDS = ['price', 'backdate', 'payment'] as const;
export type DeliveryApprovalKind = (typeof DELIVERY_APPROVAL_KINDS)[number];

export const deliveryApprovalDecisionSchema = z.object({
  decision: z.enum(['approved', 'rejected']),
  /** Required to reject; recorded either way. */
  note: z.string().trim().max(400).optional(),
});
export type DeliveryApprovalDecisionInput = z.infer<typeof deliveryApprovalDecisionSchema>;

export const deliveryApprovalSchema = z.object({
  id: uuidSchema,
  kind: z.enum(DELIVERY_APPROVAL_KINDS),
  deliveryId: uuidSchema,
  deliveryReference: z.string(),
  branchId: uuidSchema,
  branchName: z.string().nullable(),
  /** What was asked for: `{ fee }`, `{ occurredAt }` or `{ method, proofAttachmentId }`. */
  requested: z.record(z.unknown()),
  reason: z.string().nullable(),
  requestedByName: z.string().nullable(),
  requestedAt: z.string(),
  status: z.enum(['pending', 'approved', 'rejected', 'withdrawn']),
  decidedByName: z.string().nullable(),
  decidedAt: z.string().nullable(),
  decisionNote: z.string().nullable(),
});
export type DeliveryApproval = z.infer<typeof deliveryApprovalSchema>;

export const deliverySupersedeSchema = z.object({
  /** The record that stays: the one the books keep. */
  canonicalDeliveryId: uuidSchema,
  reason: z.string().trim().min(3).max(400),
});
export type DeliverySupersedeInput = z.infer<typeof deliverySupersedeSchema>;

/** A state code means nothing without its country: codes repeat across countries. */
const STATE_NEEDS_COUNTRY = { message: 'Choose a country with the state', path: ['stateCode'] };

export const deliveryFiltersSchema = z
  .object({
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().positive().max(200).default(25),
    branchId: uuidSchema.optional(),
    /**
     * Which channel created the job. Accepts a legacy alias and matches every
     * stored spelling of it — asking for `manual` finds the rows saying
     * `cenox`, which are all of them today.
     */
    originSource: deliveryOriginSchema.optional(),
    /**
     * How the record arrived: `live` or `import`.
     *
     * A separate filter because it is a separate question. The Deliveries
     * screen offered an "Imported" option that sent `originSource=import` — not
     * a channel, so the server rejected the request outright and 569 real
     * deliveries, a third of the book, were unreachable through the control
     * built to reach them.
     */
    recordOrigin: z.enum(DELIVERY_RECORD_ORIGINS).optional(),
    deliveryStatus: z.string().trim().max(40).optional(),
    search: z.string().trim().max(120).optional(),
    /**
     * Where the job happened, by its location's country — the location's own,
     * or the business's when the location never set one.
     *
     * Narrows inside what the caller may already see. Row level security has
     * decided the locations before this clause runs, so a country can never
     * widen anybody's view; it can only make it smaller.
     */
    countryCode: countryCodeSchema.optional(),
    /**
     * The location's state: its own, or the business's where it is in the
     * business's country. A code from `STATES_BY_COUNTRY`, which is only
     * unique inside a country (Nigeria and Ghana both have a `BO`), so it is
     * refused without `countryCode`. `__unset__` asks for locations with none.
     */
    stateCode: z.string().trim().min(2).max(12).optional(),
    /** Case-insensitive, like every other comparison against an imported spelling. */
    paymentStatus: z.string().trim().max(40).optional(),
    /**
     * One of the Deliveries screen's cards. `today` is the local calendar day
     * at each delivery's own location, not the server's and not the owner's.
     */
    stage: z.enum(DELIVERY_STAGES).optional(),
    sort: z.enum(DELIVERY_SORTS).default('date'),
    dir: z.enum(['asc', 'desc']).default('desc'),
    /**
     * The calendar range, in the business's timezone.
     *
     * These were missing, and because this is a plain `z.object` the unknown
     * keys were stripped silently — so `GET /deliveries?from=…&to=…` answered
     * with the entire delivery book and gave no sign the range had been
     * dropped. Against the same range the Logistics report returned 8 rows and
     * this returned every one the tenant had.
     */
    ...dateRangeFields.shape,
  })
  .refine(
    (v: { from?: string | undefined; to?: string | undefined }) => isOrderedRange(v),
    { message: ORDERED_RANGE_ISSUE.message, path: ['from'] },
  )
  .refine((v) => !v.stateCode || Boolean(v.countryCode), STATE_NEEDS_COUNTRY);
export type DeliveryFilters = z.infer<typeof deliveryFiltersSchema>;

// ── Where a delivery's money went ───────────────────────────────────────────

/**
 * Four figures that used to be one.
 *
 *   revenue            what customers were charged for jobs done and paid
 *   riderEarnings      what riders earned on those jobs (owed when earned)
 *   riderPaid          the part of those earnings a paid settlement covered
 *   companyCommission  revenue − riderEarnings: what the business keeps
 *
 * `riderOwed` is riderEarnings − riderPaid. `riderHeldCash` is fare money a
 * rider collected in cash and has not yet been settled for — the business's
 * share inside it is money in a rider's pocket that is not theirs.
 * `receivable` is work done at an authorised price and not yet paid: earned,
 * and owed by the customer, but not revenue in these figures until it is paid.
 */
export const deliveryFinanceFiguresSchema = z.object({
  jobs: z.number().int(),
  revenue: z.string(),
  riderEarnings: z.string(),
  riderPaid: z.string(),
  riderOwed: z.string(),
  companyCommission: z.string(),
  riderHeldCash: z.string(),
  receivable: z.string(),
});
export type DeliveryFinanceFigures = z.infer<typeof deliveryFinanceFiguresSchema>;

export const deliveryFinanceSummarySchema = z.object({
  from: z.string(),
  to: z.string(),
  /**
   * Every location added together.
   *
   * Only meaningful when every location trades in one currency — naira and
   * cedis added together are not a number. Kept for the readers that already
   * use it; anything showing money should read `byCurrency`.
   */
  total: deliveryFinanceFiguresSchema,
  /** One row per currency. Never adds unlike currencies together. */
  byCurrency: z.array(deliveryFinanceFiguresSchema.extend({ currencyCode: z.string() })),
  byLocation: z.array(
    deliveryFinanceFiguresSchema.extend({
      branchId: uuidSchema,
      branchName: z.string().nullable(),
      currencyCode: z.string(),
    }),
  ),
});
export type DeliveryFinanceSummary = z.infer<typeof deliveryFinanceSummarySchema>;

export const deliveryFinanceQuerySchema = z
  .object({
    from: isoDateSchema,
    to: isoDateSchema,
    branchId: uuidSchema.optional(),
    /** The same narrowing the delivery list takes, so a screen's totals and rows agree. */
    originSource: deliveryOriginSchema.optional(),
    countryCode: countryCodeSchema.optional(),
    stateCode: z.string().trim().min(2).max(12).optional(),
  })
  .refine((q) => q.from <= q.to, { message: 'The period cannot end before it starts', path: ['to'] })
  .refine((q) => !q.stateCode || Boolean(q.countryCode), STATE_NEEDS_COUNTRY);

/** The counts behind the Deliveries screen's four cards, under one set of filters. */
export const deliveryCountsSchema = z.object({
  today: z.number().int(),
  active: z.number().int(),
  completed: z.number().int(),
  history: z.number().int(),
  /**
   * Open jobs past the deadline their business set, across every page.
   *
   * Asked of the in-progress half of Active only. A delivered job awaiting
   * payment cannot be late — it arrived — so it is never counted here.
   *
   * Always 0 for a business that has configured no deadline — which is not
   * "nothing is late" but "nobody said what late means". The Active card says
   * so in those words rather than showing a reassuring zero.
   */
  overdue: z.number().int(),
  /**
   * The two halves of Active, so the card can say what it is made of.
   *
   * `inProgress` + `awaitingPayment` = `active`. They are disjoint by
   * construction: a job cannot be both still moving and already delivered.
   */
  inProgress: z.number().int(),
  /**
   * How many of `inProgress` the customer has not paid for yet.
   *
   * A subset, already inside `inProgress` — never added to `active` again.
   * `inProgressUnpaid` + `awaitingPayment` is every active job with a customer
   * payment outstanding.
   */
  inProgressUnpaid: z.number().int(),
  awaitingPayment: z.number().int(),
  /**
   * What is owed on the delivered-but-unpaid half, as a money string.
   *
   * The sum of their fees, which is the same basis as the Logistics
   * receivable — the board and the report must not describe one debt with two
   * numbers. A part-paid job's remaining balance is shown per row from the
   * sales ledger instead; it is deliberately not added in here, because a sum
   * of fees and balances would be an amount of nothing.
   */
  outstandingAmount: moneySchema,
});
export type DeliveryCounts = z.infer<typeof deliveryCountsSchema>;
